// FILE: apps/web/src/pages/UsersPage.tsx
// Users management page — list, invite, deactivate, admin reset password.
// agency_admin sees every action; coordinator gets a read-only view of the table.
// Server-driven via DataTable.

import { useState, type JSX, type FormEvent } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiError } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { useAuth } from "../hooks/useAuth.js";
import { FormField } from "../components/FormField.js";
import { ResetMfaModal, type ResetMfaTarget } from "../components/ResetMfaModal.js";
import {
  DataTable,
  useDataTable,
  type ColumnDef,
  type ListQuery,
} from "../components/DataTable/index.js";

interface UserRow {
  _id: string;
  email: string;
  full_name?: string | null;
  role: string;
  is_active: boolean;
  must_change_password?: boolean;
  invite_expires?: string | null;
  last_login_at?: string | null;
  created_at?: string;
}

interface UserListResponse { data: UserRow[]; total: number; page: number; limit: number; }

const ROLE_LABELS: Record<string, string> = {
  agency_admin: "Agency Admin",
  coordinator:  "Coordinator",
  carer:        "Care Worker",
  view_only:    "View Only",
};

const ROLE_COLOURS: Record<string, string> = {
  agency_admin: "bg-indigo-100 text-indigo-800",
  coordinator:  "bg-navy-100 text-navy-900",
  carer:        "bg-amber-100 text-amber-800",
  view_only:    "bg-slate-100 text-slate-700",
};

const ROLE_OPTIONS = [
  { label: "Agency Admin", value: "agency_admin" },
  { label: "Coordinator",  value: "coordinator" },
  { label: "Care Worker",  value: "carer" },
  { label: "View Only",    value: "view_only" },
] as const;

function RoleBadge({ role }: { role: string }): JSX.Element {
  const cls = ROLE_COLOURS[role] ?? "bg-slate-100 text-slate-700";
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {ROLE_LABELS[role] ?? role}
    </span>
  );
}

function StatusPill({ user }: { user: UserRow }): JSX.Element {
  if (!user.is_active) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Deactivated</span>;
  }
  if (user.must_change_password) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">Pending</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Active</span>;
}

interface InviteFormState {
  name: string;
  email: string;
  role: "coordinator" | "carer" | "view_only";
}

function InviteModal({ onClose, onInvited }: {
  onClose: () => void;
  onInvited: () => void;
}): JSX.Element {
  const [form, setForm] = useState<InviteFormState>({ name: "", email: "", role: "coordinator" });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiFetch("/users/invite", {
        method: "POST",
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim().toLowerCase(), role: form.role }),
      });
      toast.success(`Invitation sent to ${form.email}`);
      onInvited();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string | { fieldErrors?: Record<string, string[]> } } | null;
        if (typeof body?.error === "string") setError(body.error);
        else if (err.status === 409) setError("A user with this email already exists.");
        else setError("Could not send invitation.");
      } else {
        setError("Unable to connect.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Invite a user</h2>
        {error && (
          <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
            {error}
          </div>
        )}
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4" noValidate>
          <FormField id="invite-name" label="Full name" value={form.name}
            onChange={(v) => setForm({ ...form, name: v })} required />
          <FormField id="invite-email" type="email" label="Email" value={form.email}
            onChange={(v) => setForm({ ...form, email: v })} required autoComplete="email" />
          <div>
            <label htmlFor="invite-role" className="block text-sm font-medium text-slate-700 mb-1">Role</label>
            <select id="invite-role" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as InviteFormState["role"] })}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="coordinator">Coordinator</option>
              <option value="carer">Care Worker</option>
              <option value="view_only">View Only</option>
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
              className="flex-1 h-10 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              data-testid="users-invite-submit-btn"
              className="flex-1 h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 disabled:opacity-50">
              {submitting ? "Sending…" : "Send invite"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function buildQs(query: ListQuery): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === "") continue;
    p.set(k, String(v));
  }
  return p.toString();
}

interface ActionHandlers {
  canManage: boolean;
  isResetPending: boolean;
  isDeactivatePending: boolean;
  isActivatePending: boolean;
  onReset: (id: string) => void;
  onDeactivate: (id: string) => void;
  onActivate: (id: string) => void;
  onResetMfa: (user: UserRow) => void;
}

function ActionsCell({ user, h }: { user: UserRow; h: ActionHandlers }): JSX.Element {
  return (
    <div className="text-right space-x-2">
      <button
        onClick={() => h.onReset(user._id)}
        disabled={h.isResetPending}
        data-testid={`users-reset-${user._id}-btn`}
        className="text-xs text-navy-800 hover:text-navy-950 font-medium disabled:opacity-50">
        Reset password
      </button>
      <button
        onClick={() => h.onResetMfa(user)}
        data-testid={`users-reset-mfa-${user._id}-btn`}
        title="Clear this user's TOTP — they'll re-enroll on next login"
        className="text-xs text-amber-700 hover:text-amber-900 font-medium">
        Reset MFA
      </button>
      {user.is_active ? (
        <button
          onClick={() => h.onDeactivate(user._id)}
          disabled={h.isDeactivatePending}
          data-testid={`users-deactivate-${user._id}-btn`}
          className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50">
          Deactivate
        </button>
      ) : (
        <button
          onClick={() => h.onActivate(user._id)}
          disabled={h.isActivatePending}
          data-testid={`users-activate-${user._id}-btn`}
          className="text-xs text-emerald-700 hover:text-emerald-900 font-medium disabled:opacity-50">
          Activate
        </button>
      )}
    </div>
  );
}

function buildColumns(h: ActionHandlers): ReadonlyArray<ColumnDef<UserRow>> {
  const cols: Array<ColumnDef<UserRow>> = [
    { accessorKey: "full_name", header: "Name",
      cell: (u) => <span className="text-slate-900 font-medium">{u.full_name ?? "—"}</span> },
    { accessorKey: "email", header: "Email", sortable: true,
      cell: (u) => <span className="text-slate-600">{u.email}</span> },
    { accessorKey: "role", header: "Role", sortable: true,
      filterable: true, filterType: "select", filterOptions: ROLE_OPTIONS,
      cell: (u) => <RoleBadge role={u.role} /> },
    { accessorKey: "is_active", header: "Status", sortable: true,
      cell: (u) => <StatusPill user={u} /> },
  ];
  if (h.canManage) {
    cols.push({ accessorKey: "actions", header: "Actions", align: "right",
      cell: (u) => <ActionsCell user={u} h={h} /> });
  }
  return cols;
}

function useUserMutations(): {
  deactivate: (id: string) => void;
  activate:   (id: string) => void;
  reset:      (id: string) => void;
  isDeactivatePending: boolean;
  isActivatePending:   boolean;
  isResetPending:      boolean;
} {
  const qc = useQueryClient();
  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/users/${id}/deactivate`, { method: "PATCH" }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User deactivated"); },
    onError:   () => toast.error("Could not deactivate user"),
  });
  const activateMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/users/${id}/activate`, { method: "PATCH" }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User activated"); },
    onError:   () => toast.error("Could not activate user"),
  });
  const resetMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/users/${id}/reset-password`, { method: "POST" }),
    onSuccess: () => toast.success("Temporary password emailed"),
    onError:   () => toast.error("Could not reset password"),
  });
  return {
    deactivate: (id) => deactivateMutation.mutate(id),
    activate:   (id) => activateMutation.mutate(id),
    reset:      (id) => resetMutation.mutate(id),
    isDeactivatePending: deactivateMutation.isPending,
    isActivatePending:   activateMutation.isPending,
    isResetPending:      resetMutation.isPending,
  };
}

function userCapabilities(role: string | undefined): { canManage: boolean } {
  return {
    canManage: role === "agency_admin",
  };
}

function buildHandlers(
  canManage: boolean,
  m: ReturnType<typeof useUserMutations>,
  onResetMfa: (user: UserRow) => void,
): ActionHandlers {
  return {
    canManage,
    isResetPending:      m.isResetPending,
    isDeactivatePending: m.isDeactivatePending,
    isActivatePending:   m.isActivatePending,
    onReset:      m.reset,
    onDeactivate: m.deactivate,
    onActivate:   m.activate,
    onResetMfa,
  };
}

function ErrorBanner({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
      Failed to load users. <button className="underline" onClick={onRetry}>Retry</button>
    </div>
  );
}

function UsersHeader({ canManage, onInvite }: { canManage: boolean; onInvite: () => void }): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
        <p className="text-sm text-slate-500">Invite and manage agency staff accounts.</p>
      </div>
      {canManage && (
        <button onClick={onInvite} data-testid="users-invite-btn"
          className="h-10 px-4 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800">
          + Invite user
        </button>
      )}
    </div>
  );
}

export function UsersPage(): JSX.Element {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [resetMfaTarget, setResetMfaTarget] = useState<ResetMfaTarget | null>(null);

  const { canManage } = userCapabilities(user?.role);

  const tableState = useDataTable({
    pageSize: 20,
    defaultSort: { sortBy: "created_at", order: "desc" },
  });
  const qs = buildQs(tableState.query);
  const { data, isLoading, isError, refetch } = useQuery<UserListResponse>({
    queryKey: ["users", qs],
    queryFn: () => apiFetch<UserListResponse>(`/users?${qs}`),
  });

  const m = useUserMutations();
  const columns = buildColumns(buildHandlers(canManage, m, (u) => {
    setResetMfaTarget({ _id: u._id, email: u.email, role: u.role });
  }));

  return (
    <Shell>
      <UsersHeader canManage={canManage} onInvite={() => setShowInvite(true)} />
      {isError && <ErrorBanner onRetry={() => void refetch()} />}
      <DataTable<UserRow>
        state={tableState} columns={columns} data={data?.data ?? []} total={data?.total ?? 0}
        rowKey={(u) => u._id} ariaLabel="Users"
        isLoading={isLoading} isError={isError}
        errorMessage="Failed to load users."
        emptyState="No users yet. Invite your first teammate."
        searchPlaceholder="Search by ref or email"
      />
      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); void qc.invalidateQueries({ queryKey: ["users"] }); }}
        />
      )}
      {resetMfaTarget && (
        <ResetMfaModal
          target={resetMfaTarget}
          onClose={() => setResetMfaTarget(null)}
          onSuccess={() => { void qc.invalidateQueries({ queryKey: ["users"] }); }}
        />
      )}
    </Shell>
  );
}
